import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import { getAllProviderNames } from '../../Services/ProviderService';
import { saveLoginCreds, getLoginCredsById, loadTitles } from '../../Services/LoginCredsService';
import DotLottie from '../../Components/Common/DotLottie';

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
  { id: 60, label: 'Yet To Get' },
];

const emptyForm = {
  id: null,
  organization_id: '',
  provider_id: '',
  status_id: 1,
  title: '',
  username: '',
  password: '',
  phone: '',
  mfa_user: '',
  email: '',
  site_url: '',
  registered_url: '',
  backup_codes: [],
};

const validationSchema = Yup.object().shape({
  organization_id: Yup.string().trim().required('Organization is required'),
  provider_id: Yup.string().trim().nullable(),
  status_id: Yup.number().required('Status is required'),
  title: Yup.string().trim().required('Title is required'),
  username: Yup.string().trim().required('Username is required'),
  password: Yup.string().trim().required('Password is required'),
  phone: Yup.string().trim().nullable(),
  mfa_user: Yup.string().trim().nullable(),
  email: Yup.string().trim().email('Invalid email address').nullable(),
  backup_codes: Yup.array().of(Yup.string().trim().required('Backup code is required')),
});

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  if (Array.isArray(value?.list)) {
    return value.list;
  }

  if (Array.isArray(value?.providers)) {
    return value.providers;
  }

  if (Array.isArray(value?.organizations)) {
    return value.organizations;
  }

  return [];
};

function mapApiToForm(loginCredData) {
  return {
    id: loginCredData?.id ?? null,
    organization_id:
      loginCredData?.organization_id ?? loginCredData?.OrganizationId ?? loginCredData?.organizationId ?? '',
    provider_id:
      loginCredData?.provider_id ?? loginCredData?.ProviderId ?? loginCredData?.providerId ?? '',
    status_id: Number(loginCredData?.status_id ?? loginCredData?.StatusId ?? 1) || 1,
    title: loginCredData?.title ?? loginCredData?.Title ?? '',
    username: loginCredData?.username ?? loginCredData?.Username ?? '',
    password: loginCredData?.password ?? loginCredData?.Password ?? '',
    phone: loginCredData?.phone ?? loginCredData?.Phone ?? '',
    mfa_user: loginCredData?.mfa_user ?? loginCredData?.MfaUser ?? loginCredData?.mfaUser ?? '',
    email: loginCredData?.email ?? loginCredData?.Email ?? '',
    site_url: loginCredData?.site_url ?? loginCredData?.SiteUrl ?? loginCredData?.siteUrl ?? '',
    registered_url: loginCredData?.registered_url ?? loginCredData?.RegisteredUrl ?? loginCredData?.registeredUrl ?? '',
    backup_codes: normalizeArray(loginCredData?.backup_codes ?? loginCredData?.BackupCodes ?? []),
  };
}

function buildPayload(values) {
  return {
    id: values.id || null,
    organization_id: values.organization_id || null,
    provider_id: values.provider_id || null,
    status_id: values.status_id,
    title: values.title,
    username: values.username,
    password: values.password || null,
    phone: values.phone || null,
    mfa_user: values.mfa_user || null,
    email: values.email || null,
    url : values.site_url || null,
    site_url: values.site_url || null,
    registered_url: values.registered_url || null,
    backup_codes: normalizeArray(values.backup_codes),
    type: values.id ? 'update' : 'create',
  };
}

export default function LoginCredsForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken]
  );

  const [loading, setLoading] = useState(isEdit);
  const [organizations, setOrganizations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [titleOptions, setTitleOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState(false);
  const [titlesLoading, setTitlesLoading] = useState(false);

  const formik = useFormik({
    initialValues: useMemo(() => emptyForm, []),
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        helpers.setSubmitting(true);
        const payload = buildPayload(values);
        const response = await saveLoginCreds(payload, token);

        if (response?.is_success) {
          toast.success(response?.message || 'Login credential saved successfully');
          const loginCredId = response?.data?.Id || response?.data || id;
          navigate(APPRoutes.LOGIN_CREDS_VIEW.replace(':id', loginCredId));
        } else if (response?.status_code === 500) {
          toast.error('Internal Server Error. Please contact support.');
        } else {
          toast.error(response?.message || 'Failed to save login credential');
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Failed to save login credential');
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const loadTitleOptions = async () => {
    try {
      setTitlesLoading(true);
      const response = await loadTitles(token);
      const items = normalizeArray(response?.data ?? response?.titles ?? []);
      setTitleOptions(items);
    } catch (error) {
      console.error('Failed to load titles:', error);
      setTitleOptions([]);
    } finally {
      setTitlesLoading(false);
    }
  };

  React.useEffect(() => {
    if (!titleOptions.length || !formik.values.title) return;

    const matchedTitle = titleOptions.find(
      (item) => (item.name ?? item.Name ?? item.title ?? '') === formik.values.title,
    );

    if (!matchedTitle) return;

    const targetSiteUrl = matchedTitle.site_url ?? matchedTitle.SiteUrl ?? '';
    const targetRegUrl = matchedTitle.registration_url ?? matchedTitle.registrationUrl ?? '';

    if (!formik.values.site_url && targetSiteUrl) {
      formik.setFieldValue('site_url', targetSiteUrl);
    }
    if (!formik.values.registered_url && targetRegUrl) {
      formik.setFieldValue('registered_url', targetRegUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleOptions, formik.values.title]);

  const loadOrganizations = async () => {
    try {
      setOrgLoading(true);
      const response = await getAllOrganizationNames(token);
      const items =
        response?.data?.login_creds ?? response?.data ?? response?.Organizations ?? response;
      setOrganizations(normalizeArray(items));
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const loadProviders = async () => {
    try {
      setProviderLoading(true);
      const response = await getAllProviderNames(token);
      const items = normalizeArray(
        response?.data?.providers ?? response?.data ?? response?.providers ?? response?.result ?? response,
      );
      setProviders(items);
    } catch (error) {
      console.error('Failed to load providers:', error);
      setProviders([]);
    } finally {
      setProviderLoading(false);
    }
  };

  const loadLoginCred = async () => {
    if (!isEdit) return;

    try {
      setLoading(true);
      const response = await getLoginCredsById(id, token);
      if (response?.is_success) {
        formik.setValues(mapApiToForm(response?.data ?? response));
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please contact support.');
      } else {
        toast.error(response?.message || 'Failed to load login credential');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Failed to load login credential');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadOrganizations();
    loadProviders();
    loadTitleOptions();
    if (isEdit) {
      loadLoginCred();
    }
  }, [token, id]);

  const requiredPermission = isEdit ? PermissionCodes.LOGIN_CREDS_EDIT : PermissionCodes.LOGIN_CREDS_CREATE;
  const canAccessPage = permissionHas(userPermissionCodes, requiredPermission, decodedToken);

  if (!canAccessPage) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">You do not have permission to access this page.</div>
      </div>
    );
  }

  const renderAsterisk = () => <span className="text-danger">*</span>;

  const showFieldError = (field) => Boolean((formik.touched[field] || formik.submitCount > 0) && formik.errors[field]);

  const handleBackupCodeChange = (index, value) => {
    const updatedCodes = [...formik.values.backup_codes];
    updatedCodes[index] = value;
    formik.setFieldValue('backup_codes', updatedCodes);
  };

  const addBackupCode = () => {
    formik.setFieldValue('backup_codes', [...formik.values.backup_codes, '']);
  };

  const removeBackupCode = (index) => {
    const updatedCodes = [...formik.values.backup_codes];
    updatedCodes.splice(index, 1);
    formik.setFieldValue('backup_codes', updatedCodes);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <DotLottie />
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <div className="rounded-4 bg-white p-3 p-md-4 shadow-sm border-0">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light border rounded-circle shadow-sm"
              onClick={() => navigate(-1)}
              style={{ width: 40, height: 40 }}
            >
              &larr;
            </button>
            <div>
              <h3 className="fw-bold mb-0 hc-text-primary">
                {isEdit ? 'Edit Login Credential' : 'Create Login Credential'}
              </h3>
              <small className="text-muted">Manage login credential settings for the platform.</small>
            </div>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
            <h6 className="fw-bold hc-text-primary mb-3">Credential Details</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <SearchableDropdown
                  label={
                    <>
                      Title
                    </>
                  }
                  placeholder="Search or type a title"
                  options={titleOptions.map((option) => ({
                    value: option.name ?? option.Name ?? option.title ?? '',
                    label: option.name ?? option.Name ?? option.title ?? '',
                  }))}
                  value={formik.values.title}
                  onChange={(value) => {
                    const selectedTitle = titleOptions.find(
                      (item) => (item.name ?? item.Name ?? item.title ?? '') === value,
                    );
                    formik.setFieldValue('title', value || '');
                    if (selectedTitle) {
                      formik.setFieldValue('site_url', selectedTitle.site_url ?? selectedTitle.SiteUrl ?? '');
                      formik.setFieldValue(
                        'registered_url',
                        selectedTitle.registration_url ?? selectedTitle.registrationUrl ?? '',
                      );
                    }
                  }}
                  onInputChange={(value) => {
                    formik.setFieldValue('title', value || '');
                    if (!titleOptions.some((item) => (item.name ?? item.Name ?? item.title ?? '') === value)) {
                      formik.setFieldValue('site_url', '');
                      formik.setFieldValue('registered_url', '');
                    }
                  }}
                  loading={titlesLoading}
                  disabled={titlesLoading}
                  required
                  allowCustom
                  emptyMessage="No titles found"
                />
                <div className="invalid-feedback d-block">
                  {showFieldError('title') ? formik.errors.title : ''}
                </div>
              </div>

              <div className="col-md-6">
                <SearchableDropdown
                  label="Status"
                  placeholder="Select Status"
                  options={STATUS_OPTIONS.map((item) => ({ value: item.id, label: item.label }))}
                  value={formik.values.status_id}
                  onChange={(value) => formik.setFieldValue('status_id', value !== '' ? Number(value) : '')}
                  showSearch={false}
                  required
                  error={formik.touched.status_id && formik.errors.status_id ? formik.errors.status_id : ''}
                />
              </div>
            </div>

            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Username {renderAsterisk()}
                </label>
                <input
                  name="username"
                  placeholder="Username"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.username && formik.errors.username ? 'is-invalid' : ''}`}
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.touched.username && formik.errors.username ? formik.errors.username : ''}</div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Password {renderAsterisk()}
                </label>
                <input
                  name="password"
                  type="password"
                  placeholder={isEdit ? 'Enter new password or leave blank' : 'Password'}
                  className={`form-control rounded-3 shadow-sm ${showFieldError('password') ? 'is-invalid' : ''}`}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  autoComplete="new-password"
                />
                <div className="invalid-feedback">{showFieldError('password') ? formik.errors.password : ''}</div>
              </div>
            </div>

            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Associated Phone#</label>
                <input
                  name="phone"
                  placeholder="(555) 123-4567"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Associated Email</label>
                <input
                  name="email"
                  placeholder="example@domain.com"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.touched.email && formik.errors.email ? formik.errors.email : ''}</div>
              </div>

            </div>

            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">MFA User</label>
                <input
                  name="mfa_user"
                  placeholder="MFA username"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.mfa_user}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <SearchableDropdown
                  label={
                    <>
                      Organization
                    </>
                  }
                  placeholder="Select organization"
                  options={organizations.map((org) => ({
                    value: org.id ?? org.OrganizationId ?? org.organization_id ?? org.Id ?? org.value ?? '',
                    label: org.organization_name ?? org.OrganizationName ?? org.name ?? org.group_office_name ?? 'Organization',
                  }))}
                  value={formik.values.organization_id}
                  onChange={(value) => formik.setFieldValue('organization_id', value || '')}
                  loading={orgLoading}
                  disabled={orgLoading}
                  required
                  emptyMessage="No organizations found"
                />
                <div className="invalid-feedback d-block">
                  {formik.touched.organization_id && formik.errors.organization_id ? formik.errors.organization_id : ''}
                </div>
              </div>

              <div className="col-md-6">
                <SearchableDropdown
                  label={
                    <>
                      Provider
                    </>
                  }
                  placeholder="Select provider"
                  options={providers.map((provider) => ({
                    value: provider.id ?? provider.ProviderId ?? provider.provider_id ?? provider.Id ?? provider.value ?? '',
                    label: provider.name ?? provider.ProviderName ?? provider.provider_name ?? provider.label ?? 'Provider',
                  }))}
                  value={formik.values.provider_id}
                  onChange={(value) => formik.setFieldValue('provider_id', value || '')}
                  loading={providerLoading}
                  disabled={providerLoading}
                  emptyMessage="No providers found"
                />
                <div className="invalid-feedback d-block">
                  {formik.touched.provider_id && formik.errors.provider_id ? formik.errors.provider_id : ''}
                </div>
              </div>
            </div>

            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Site URL</label>
                <input
                  name="site_url"
                  placeholder="https://"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.site_url}
                  readOnly
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Registered URL</label>
                <input
                  name="registered_url"
                  placeholder="https://"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.registered_url}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="p-4 rounded-4 border bg-light-subtle">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="fw-bold hc-text-primary mb-0">Backup Codes</h6>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addBackupCode}>
                Add Code
              </button>
            </div>

            {formik.values.backup_codes.length === 0 ? (
              <div className="text-secondary small mb-3">No backup codes added yet.</div>
            ) : null}

            <div className="row g-3">
              {formik.values.backup_codes.map((code, index) => (
                <div className="col-12" key={`backup-code-${index}`}>
                  <div className="input-group gap-3">
                    <input
                      type="text"
                      name={`backup_codes[${index}]`}
                      placeholder="Backup code"
                      className={`form-control rounded-3 shadow-sm ${formik.touched.backup_codes && formik.errors.backup_codes ? 'is-invalid' : ''}`}
                      value={code}
                      onChange={(event) => handleBackupCodeChange(index, event.target.value)}
                      onBlur={formik.handleBlur}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger rounded-3"
                      onClick={() => removeBackupCode(index)}
                    >
                      Remove
                    </button>
                    {formik.touched.backup_codes && formik.errors.backup_codes ? (
                      <div className="invalid-feedback d-block">
                        {Array.isArray(formik.errors.backup_codes)
                          ? formik.errors.backup_codes[index]
                          : formik.errors.backup_codes}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-end mt-4">
            <button
              type="submit"
              className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
